/**
 * Text box for the Texti tool: a textarea at the tap point, kept inside the VISUAL viewport so the
 * iOS keyboard never covers it. "Lokið" (or leaving the box) commits, "Hætta við" discards.
 */
import { Check, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { createPortal } from 'react-dom';
import { MAX_TEXT_CHARS } from '../../shared/annotations';
import { Button } from '../components/ui';
import { useT } from '../i18n';
import { clamp } from './geometry';
import { TEXT_FONT } from './shapes';
import type { Annotate } from './useAnnotate';

interface Viewport {
  left: number;
  top: number;
  width: number;
  height: number;
}

function readViewport(): Viewport {
  const v = typeof window !== 'undefined' ? window.visualViewport : null;
  if (v) return { left: v.offsetLeft, top: v.offsetTop, width: v.width, height: v.height };
  return { left: 0, top: 0, width: window.innerWidth, height: window.innerHeight };
}

function useVisualViewport(): Viewport {
  const [vp, setVp] = useState(readViewport);
  useEffect(() => {
    const v = window.visualViewport;
    const on = () => setVp(readViewport());
    v?.addEventListener('resize', on);
    v?.addEventListener('scroll', on);
    window.addEventListener('resize', on);
    return () => {
      v?.removeEventListener('resize', on);
      v?.removeEventListener('scroll', on);
      window.removeEventListener('resize', on);
    };
  }, []);
  return vp;
}

/** Buttons keep the focus in the textarea (no blur-commit before their click). */
const keepFocus = (e: ReactPointerEvent) => e.preventDefault();

export function TextEditor({ ann }: { ann: Annotate }) {
  const t = useT();
  const d = ann.text;
  const vp = useVisualViewport();
  const boxRef = useRef<HTMLDivElement>(null);
  const [boxH, setBoxH] = useState(170);
  const blurTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setBoxH(el.offsetHeight));
    ro.observe(el);
    return () => ro.disconnect();
  }, [d !== null]);

  useEffect(() => () => clearTimeout(blurTimer.current), []);

  if (!d) return null;
  const width = Math.min(440, vp.width - 16);
  const left = clamp(d.client.x - 14, vp.left + 8, vp.left + vp.width - width - 8);
  const top = clamp(d.client.y - 14, vp.top + 8, Math.max(vp.top + 8, vp.top + vp.height - boxH - 8));
  const fontSize = clamp(d.px, 17, 40);

  return createPortal(
    <div
      ref={boxRef}
      data-annot-ignore
      className="fixed z-[55] rounded-2xl bg-[var(--surface)] p-2 shadow-[var(--shadow-sheet)] ring-1 ring-[var(--line)]"
      style={{ left, top, width }}
    >
      <textarea
        ref={ann.textAreaRef}
        value={d.text}
        maxLength={MAX_TEXT_CHARS}
        rows={Math.min(6, Math.max(2, d.text.split('\n').length + 1))}
        aria-label={t('annotate.text_label')}
        placeholder={t('annotate.text_placeholder')}
        onChange={(e) => ann.setTextValue(e.target.value)}
        onFocus={() => clearTimeout(blurTimer.current)}
        onBlur={() => {
          clearTimeout(blurTimer.current);
          // Commit when the box is left (keyboard "Done", tap elsewhere) unless a button took over.
          blurTimer.current = setTimeout(() => {
            if (document.activeElement !== ann.textAreaRef.current) ann.commitText();
          }, 300);
        }}
        className="block w-full resize-none rounded-xl bg-white px-3 py-2 leading-tight ring-1 ring-[var(--line)] outline-none focus:ring-2 focus:ring-[var(--focus)]"
        style={{ color: d.color, fontSize, fontFamily: TEXT_FONT, fontWeight: 500 }}
      />
      <div className="mt-2 flex items-center gap-2">
        <span className="flex-1 pl-1 text-[13px] text-[var(--muted)] tabular-nums">
          {d.text.length}/{MAX_TEXT_CHARS}
        </span>
        {d.id ? (
          <Button
            variant="ghost"
            icon={Trash2}
            onPointerDown={keepFocus}
            onClick={() => {
              ann.setTextValue('');
              ann.commitText();
            }}
          >
            {t('common.delete')}
          </Button>
        ) : null}
        <Button variant="secondary" onPointerDown={keepFocus} onClick={ann.cancelText}>
          {t('common.cancel')}
        </Button>
        <Button icon={Check} onPointerDown={keepFocus} onClick={ann.commitText}>
          {t('common.done')}
        </Button>
      </div>
    </div>,
    document.body,
  );
}
